import axios from 'axios';
import OktaAuth from '@okta/okta-auth-js';
import _omit from 'lodash/omit';
import { captureException } from '@sentry/react';
import { OMITTED_URLS } from '@app/constants/axios';
import { getOktaConfig } from '@app/okta.config';
import navigationService from '@app/services/navigationService';

const oktaAuth = new OktaAuth(getOktaConfig().oidc);
const baseConfig = {
  headers: {
    'Content-Type': 'application/json',
  },
  retry: 1,
  withCredentials: true,
};

const coreClient = axios.create({
  baseURL: '/api/core',
  ...baseConfig,
});

const insightsClient = axios.create({
  baseURL: `/api/reporting`,
  ...baseConfig,
});

const cmsClient = axios.create({
  baseURL: `/api/bennie-cms`,
  ...baseConfig,
});

const uiServicesClient = axios.create({
  baseURL: `/api/ui-services`,
  ...baseConfig,
});

const marketplaceApiClient = axios.create({
  baseURL: `/api/marketplace-partners`,
  ...baseConfig,
});

const findADoctorClient = axios.create({
  baseURL: `/api/findcare/v1`,
  ...baseConfig,
});

const umsClient = axios.create({
  baseURL: `/api/usermanagement/v1/users`,
  ...baseConfig,
});

const requestInterceptor = config => {
  const accessToken = oktaAuth.getAccessToken();
  const omitHeaders = OMITTED_URLS.some(omittedUrl =>
    config.url.includes(omittedUrl),
  );

  if (omitHeaders) {
    return {
      ...config,
      // Excludes Authorization header for endpoints listed in the
      // OMITTED_URLS array
      headers: _omit(config.headers, 'Authorization'),
    };
  }

  return {
    ...config,
    headers: {
      ...config.headers,
      Authorization: `Bearer ${accessToken}`,
    },
  };
};

const handleAuthFailure = () => {
  // Use navigation service to avoid circular dependency
  navigationService.triggerAuthFailure();
};

const errorInterceptor = client => async err => {
  const { config, response } = err;
  if (!config || !response) {
    return Promise.reject(err);
  }

  const retryCodes = [401];

  // Check if this is a retry-eligible error and we have retries left
  if (config.retry && retryCodes.includes(response.status)) {
    try {
      const existingToken = oktaAuth.tokenManager.get('accessToken');
      
      if (!existingToken) {
        captureException(new Error('No access token available for renewal'), {
          tags: { context: 'token_refresh_failure', reason: 'no_token' },
          extra: {
            message:
              'Token renewal attempted but no access token exists, triggering re-authentication',
          },
        });
        handleAuthFailure();
        return Promise.reject(
          new axios.Cancel('Request cancelled due to authentication failure'),
        );
      }

      const token = await oktaAuth.tokenManager
        .renew('accessToken')
        .catch(error => {
          return Promise.reject(error);
        });

      if (!token || !token.accessToken) {
        captureException(new Error('Token renewal returned invalid token'), {
          tags: { context: 'token_refresh_failure', reason: 'invalid_token' },
          extra: {
            message: 'Token renewal succeeded but returned invalid token',
          },
        });
        handleAuthFailure();
        return Promise.reject(
          new axios.Cancel('Request cancelled due to authentication failure'),
        );
      }

      config.retry = config.retry ? config.retry - 1 : 0;
      config.headers.Authorization = `Bearer ${token.accessToken}`;
      config.retries = config.retries || 1;

      return new Promise(resolve => {
        resolve(client({ ...config, headers: config.headers.toJSON() }));
      });
    } catch (tokenError) {
      // If token refresh fails and this is a 401, it's an auth failure
      if (response.status === 401) {
        captureException(tokenError, {
          tags: { context: 'token_refresh_failure', reason: 'renew_error' },
          extra: {
            message:
              'Token refresh failed for 401 response, redirecting to logout',
            errorMessage: tokenError?.message,
          },
        });
        handleAuthFailure();
        return Promise.reject(
          new axios.Cancel('Request cancelled due to authentication failure'),
        );
      }
      // For other errors, just continue to normal error handling
    }
  }

  if ((response.status === 401 && !config.retry) || response.status === 403) {
    handleAuthFailure();
    return Promise.reject(
      new axios.Cancel('Request cancelled due to authentication failure'),
    );
  }

  captureException(err);
  return Promise.reject(err);
};

[
  coreClient,
  insightsClient,
  cmsClient,
  uiServicesClient,
  marketplaceApiClient,
  findADoctorClient,
  umsClient,
].forEach(client => {
  client.interceptors.request.use(requestInterceptor);
  client.interceptors.response.use(null, errorInterceptor(client));
});

export {
  coreClient,
  insightsClient,
  cmsClient,
  uiServicesClient,
  marketplaceApiClient,
  findADoctorClient,
  umsClient,
};
